import React from 'react';
import { useTranslation } from 'react-i18next';
import { GraphQLFormattedError } from 'graphql';
import { Values } from '@lib/interfaces/form';
import Form, {
  ActionsProps,
  FormRef,
} from '@lib/components/ReactHookForm/FormContainer';
import replaceNullsWithUndefined from '@lib/utils/replaceNullsWithUndefined';
import { getAreaFields } from 'routes/Sites/utils/getSiteFormFields';
import getAreaEmptyDefaultValues from 'routes/Sites/utils/getAreaEmptyDefaultValues';
import { SubmitAction } from '@lib/enums/form';
import { GetLoadOptions } from '@lib/components/Select/types';
import AreaCreateFormActions from './AreaCreateFormActions';

export interface FormProps {
  areaData?: Values;
  formNewAreaRef?: React.RefObject<FormRef | undefined>;
  // Optional: callers that don't expose the documents field (e.g. when EDM
  // access is locked) omit these to hide it — see getAreaFields.
  getDocumentationsLoadOptions?: GetLoadOptions;
  graphQLErrors?: readonly GraphQLFormattedError[];
  loading: boolean;
  onCancel: () => void;
  onSubmit: (values: Values) => void;
  openDocumentsModal?: () => void;
  submitNewAreaRef: React.RefObject<SubmitAction | undefined>;
}

function AreaCreateForm(props: FormProps) {
  const {
    areaData,
    formNewAreaRef,
    getDocumentationsLoadOptions,
    graphQLErrors,
    loading,
    onCancel,
    onSubmit,
    openDocumentsModal,
    submitNewAreaRef,
  } = props;
  const { t } = useTranslation();
  const fields = getAreaFields({
    getDocumentationsLoadOptions,
    openDocumentsModal,
    t,
  });

  const actions: (actionsProps: ActionsProps) => React.ReactElement = (
    actionsProps,
  ) => (
    <AreaCreateFormActions
      {...actionsProps}
      submitNewAreaRef={submitNewAreaRef}
      onCancel={onCancel}
    />
  );

  const defaultValues = replaceNullsWithUndefined(
    areaData || getAreaEmptyDefaultValues(),
  );

  return (
    <Form
      actions={actions}
      actionsComponentProps={{ submitNewAreaRef, onCancel, loading }}
      defaultValues={defaultValues}
      fields={fields}
      formId="area"
      formRef={formNewAreaRef}
      graphQLErrors={graphQLErrors}
      graphQLErrorsSkipPath={['areas', '0']}
      loading={loading}
      onSubmit={onSubmit}
    />
  );
}

export default AreaCreateForm;
